import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AlertProvider } from './context/AlertContext';
import { ConfirmProvider } from './context/ConfirmContext';
import ProtectedRoute from './components/ProtectedRoute';

// Standard Pages
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Landing from './pages/Landing';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Signup from './pages/Signup';
import FranchiseRegistration from './pages/FranchiseRegistration';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Packages from './pages/Packages';
import Earnings from './pages/Earnings';
import FranchiseTree from './pages/FranchiseTree';
import Bank from './pages/Bank';
import Profile from './pages/Profile';
import UserFranchiseApply from './pages/UserFranchiseApply';
import UserPackages from './pages/UserPackages';
import MyDirect from './pages/MyDirect';
import MyDownlines from './pages/MyDownlines';
import DownlineReport from './pages/DownlineReport';
import Cart from './pages/Cart';
// import DownlineSalesReport from './pages/DownlineSalesReport';
// import UserProducts from './pages/UserProducts';

// E-Pin Pages
import EPinApply from './pages/EPinApply';
import EPinReport from './pages/EPinReport';
import EPinUnused from './pages/EPinUnused';
import EPinUsed from './pages/EPinUsed';
import ShoppingWallet from './pages/ShoppingWallet';
import ProductOrders from './pages/ProductOrders';

// Income Pages
import DirectFranchiseIncome from './pages/DirectFranchiseIncome';
import MatchingIncome from './pages/MatchingIncome';
import PerformanceIncome from './pages/PerformanceIncome';
import BinaryPayout from './pages/BinaryPayout';
import BinaryIncome from './pages/BinaryIncome';
import FranchiseBonusIncome from './pages/FranchiseBonusIncome';
import IncomeWallet from './pages/IncomeWallet';

// Franchise Pages
import BuyEPin from './pages/franchise/BuyEPin';
import TransferEPin from './pages/franchise/TransferEPin';
import Stock from './pages/franchise/Stock';
import SalesReport from './pages/franchise/SalesReport';
import Commission from './pages/franchise/Commission';
import FranchiseProfile from './pages/franchise/Profile';
import FranchiseTransactions from './pages/franchise/Transactions';
import FranchiseOrders from './pages/franchise/Orders';

// Admin Pages
import Users from './pages/admin/Users';
import Approvals from './pages/admin/Approvals';
import FranchiseApprovables from './pages/admin/FranchiseApprovables';
import UserFranchiseApplications from './pages/admin/UserFranchiseApplications';
import FranchisePackages from './pages/admin/FranchisePackages';
import BlockedUsers from './pages/admin/BlockedUsers';
import Franchises from './pages/admin/Franchises';
import CreateFranchise from './pages/admin/CreateFranchise';
import AdminFranchiseTree from './pages/admin/FranchiseTree';
import AdminTeams from './pages/admin/Teams';
import WalletHistory from './pages/admin/WalletHistory';
import Payouts from './pages/admin/Payouts';
import AdminBinaryPayouts from './pages/admin/BinaryPayouts';
import WithdrawalRequests from './pages/admin/WithdrawalRequests';
import ContactRequests from './pages/admin/ContactRequests';
import NoticeManagement from './pages/admin/NoticeManagement';
import VerifyAdminEmail from './pages/admin/VerifyEmail';
import Transactions from './pages/admin/Transactions';
import AdminSales from './pages/admin/reports/Sales';
import AdminIncome from './pages/admin/reports/Income';
import Settings from './pages/admin/Settings';
import EPinRequests from './pages/admin/EPinRequests';
import BillSettings from './pages/admin/BillSettings';
import InvoiceManagement from './pages/admin/InvoiceManagement';

import ProductCategories from './pages/admin/products/ProductCategories';
import ProductsManagement from './pages/admin/products/ProductsManagement';
import PackagesManagement from './pages/admin/products/PackagesManagement';
import BogoManagement from './pages/admin/products/BogoManagement';
import LandingProducts from './pages/admin/LandingProducts';
// import DispatchRequests from './pages/admin/DispatchRequests';
// import UserOrders from './pages/UserOrders';
import Shop from './pages/Shop';
import Checkout from './pages/Checkout';
import ShopOrders from './pages/admin/ShopOrders';
import HistoryWeek from './pages/admin/HistoryWeek';
import PayoutHistory from './pages/admin/PayoutHistory';
import TdsReport from './pages/admin/TdsReport';


import { AdminProvider } from './context/AdminContext';
import KycReminderModal from './components/KycReminderModal';

function App() {
  return (
    <AuthProvider>
      <AlertProvider>
        <ConfirmProvider>
          <AdminProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                {/* Root Route - Landing Page */}
                <Route path="/" element={<Landing />} />

                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/franchise-registration" element={<FranchiseRegistration />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* Protected Core Routes */}
                <Route path="/register" element={<ProtectedRoute><Register /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/kyc" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/bank" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/change-password" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                {/* User Specific Routes */}
                <Route path="/packages" element={<ProtectedRoute><Packages /></ProtectedRoute>} />
                <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
                <Route path="/bank" element={<ProtectedRoute><Bank /></ProtectedRoute>} />

                <Route path="/epin/apply" element={<ProtectedRoute><EPinApply /></ProtectedRoute>} />
                <Route path="/epin/request-report" element={<ProtectedRoute><EPinReport /></ProtectedRoute>} />
                <Route path="/epin/unused" element={<ProtectedRoute><EPinUnused /></ProtectedRoute>} />
                <Route path="/epin/used" element={<ProtectedRoute><EPinUsed /></ProtectedRoute>} />
                {/* Old routes for backward compatibility */}
                <Route path="/epin/shopping-wallet" element={<ProtectedRoute><ShoppingWallet /></ProtectedRoute>} />
                <Route path="/epin/product-orders" element={<ProtectedRoute><ProductOrders /></ProtectedRoute>} />



                <Route path="/income/direct-franchise" element={<ProtectedRoute><DirectFranchiseIncome /></ProtectedRoute>} />
                <Route path="/income/matching" element={<ProtectedRoute><MatchingIncome /></ProtectedRoute>} />
                <Route path="/income/performance" element={<ProtectedRoute><PerformanceIncome /></ProtectedRoute>} />
                <Route path="/income/franchise-bonus" element={<ProtectedRoute><FranchiseBonusIncome /></ProtectedRoute>} />
                <Route path="/income/binary" element={<ProtectedRoute><BinaryIncome /></ProtectedRoute>} />
                <Route path="/income/binary-payout" element={<ProtectedRoute><BinaryPayout /></ProtectedRoute>} />
                <Route path="/income/wallet" element={<ProtectedRoute><IncomeWallet /></ProtectedRoute>} />

                {/* User Franchise Application */}
                <Route path="/user/franchise/apply" element={<ProtectedRoute><UserFranchiseApply /></ProtectedRoute>} />

                {/* My Network Routes */}
                <Route path="/network/direct" element={<ProtectedRoute><MyDirect /></ProtectedRoute>} />
                <Route path="/network/downlines" element={<ProtectedRoute><MyDownlines /></ProtectedRoute>} />
                <Route path="/network/report" element={<ProtectedRoute><DownlineReport /></ProtectedRoute>} />

                {/* Products & Packages */}
                <Route path="/products/packages" element={<ProtectedRoute><UserPackages /></ProtectedRoute>} />
                <Route path="/products/shopping-wallet" element={<ProtectedRoute><ShoppingWallet /></ProtectedRoute>} />
                <Route path="/products/order-report" element={<ProtectedRoute><ProductOrders /></ProtectedRoute>} />
                {/* <Route path="/products/catalog" element={<ProtectedRoute><UserProducts /></ProtectedRoute>} /> */}
                {/* <Route path="/products/orders" element={<ProtectedRoute><UserOrders /></ProtectedRoute>} /> */}
                <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><ProductOrders /></ProtectedRoute>} />

                {/* Franchise Routes */}
                <Route path="/franchise/buy-epin" element={<ProtectedRoute><BuyEPin /></ProtectedRoute>} />
                <Route path="/franchise/transfer" element={<ProtectedRoute><TransferEPin /></ProtectedRoute>} />
                <Route path="/franchise/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
                <Route path="/franchise/sales-report" element={<ProtectedRoute><SalesReport /></ProtectedRoute>} />
                <Route path="/franchise/commission" element={<ProtectedRoute><Commission /></ProtectedRoute>} />
                <Route path="/franchise/tree" element={<ProtectedRoute><FranchiseTree /></ProtectedRoute>} />
                <Route path="/franchise/profile" element={<ProtectedRoute><FranchiseProfile /></ProtectedRoute>} />
                <Route path="/franchise/transactions" element={<ProtectedRoute><FranchiseTransactions /></ProtectedRoute>} />
                <Route path="/franchise/orders" element={<ProtectedRoute><FranchiseOrders /></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
                <Route path="/admin/teams" element={<ProtectedRoute><AdminTeams /></ProtectedRoute>} />
                <Route path="/admin/wallet-history" element={<ProtectedRoute><WalletHistory /></ProtectedRoute>} />
                <Route path="/admin/approvals" element={<ProtectedRoute><Approvals /></ProtectedRoute>} />
                <Route path="/admin/verify-email" element={<ProtectedRoute><VerifyAdminEmail /></ProtectedRoute>} />
                <Route path="/admin/franchise-approvables" element={<ProtectedRoute><FranchiseApprovables /></ProtectedRoute>} />
                <Route path="/admin/blocked" element={<ProtectedRoute><BlockedUsers /></ProtectedRoute>} />

                <Route path="/admin/franchises" element={<ProtectedRoute><Franchises /></ProtectedRoute>} />
                <Route path="/admin/create-franchise" element={<ProtectedRoute><CreateFranchise /></ProtectedRoute>} />
                <Route path="/admin/user-franchise-applications" element={<ProtectedRoute><UserFranchiseApplications /></ProtectedRoute>} />
                <Route path="/admin/franchise-tree" element={<ProtectedRoute><AdminFranchiseTree /></ProtectedRoute>} />
                <Route path="/admin/franchise-packages" element={<ProtectedRoute><FranchisePackages /></ProtectedRoute>} />

                {/* Product Management */}
                <Route path="/admin/products/categories" element={<ProtectedRoute><ProductCategories /></ProtectedRoute>} />
                <Route path="/admin/products/list" element={<ProtectedRoute><ProductsManagement /></ProtectedRoute>} />
                <Route path="/admin/products/packages" element={<ProtectedRoute><PackagesManagement /></ProtectedRoute>} />
                <Route path="/admin/products/bogo" element={<ProtectedRoute><BogoManagement /></ProtectedRoute>} />
                <Route path="/admin/landing-products" element={<ProtectedRoute><LandingProducts /></ProtectedRoute>} />
                <Route path="/admin/shop-orders" element={<ProtectedRoute><ShopOrders /></ProtectedRoute>} />

                <Route path="/admin/payouts" element={<ProtectedRoute><Payouts /></ProtectedRoute>} />
                <Route path="/admin/binary-payouts" element={<ProtectedRoute><AdminBinaryPayouts /></ProtectedRoute>} />
                <Route path="/admin/withdrawals" element={<ProtectedRoute><WithdrawalRequests /></ProtectedRoute>} />
                <Route path="/admin/history-week" element={<ProtectedRoute><HistoryWeek /></ProtectedRoute>} />
                <Route path="/admin/reports/payout-history" element={<ProtectedRoute><PayoutHistory /></ProtectedRoute>} />
                <Route path="/admin/reports/tds" element={<ProtectedRoute><TdsReport /></ProtectedRoute>} />

                <Route path="/admin/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
                <Route path="/admin/epin-requests" element={<ProtectedRoute><EPinRequests /></ProtectedRoute>} />

                <Route path="/admin/reports/sales" element={<ProtectedRoute><AdminSales /></ProtectedRoute>} />
                <Route path="/admin/reports/income" element={<ProtectedRoute><AdminIncome /></ProtectedRoute>} />

                <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/admin/contact-requests" element={<ProtectedRoute><ContactRequests /></ProtectedRoute>} />
                <Route path="/admin/notices" element={<ProtectedRoute><NoticeManagement /></ProtectedRoute>} />
                <Route path="/admin/bill-settings" element={<ProtectedRoute><BillSettings /></ProtectedRoute>} />
                <Route path="/admin/invoices" element={<ProtectedRoute><InvoiceManagement /></ProtectedRoute>} />


                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <KycReminderModal />
            </Router>
          </AdminProvider>
        </ConfirmProvider>
      </AlertProvider>
    </AuthProvider>
  );
}

export default App;
